import type { RealEstateKpis, StoreCaOverDays } from '@gozoki/api-types'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import {
  createStoreProject,
  deleteStoreProject,
  deleteStoreProjectDocument,
  deleteStoreProjectPhoto,
  renameStoreProjectDocument,
  updateStoreProject,
  updateStoreProjectPosition,
  updateStoreProjectStatus,
  uploadStoreProjectDocument,
  uploadStoreProjectPhoto,
} from '../mutations/storeProjects.mutations'
import {
  fetchRealEstateKpis,
  fetchStoreCaOverDays,
  fetchStoreProjects,
  type FetchRealEstateKpisParams,
  type StoreProjectResponse,
} from '../queries/storeProjects.queries'

export const useStoreProjects = () =>
  useQuery({
    queryKey: ['store-projects'],
    queryFn: fetchStoreProjects,
  })

export const useRealEstateKpis = (
  params?: FetchRealEstateKpisParams,
): UseQueryResult<RealEstateKpis, unknown> =>
  useQuery({
    queryKey: [
      'real-estate-kpis',
      params?.openStoresComparisonDays ?? 'default',
      params?.storeIds ?? [],
      params?.storeAgeFilter ?? 'default',
    ],
    queryFn: () => fetchRealEstateKpis(params),
  })

export const useStoreCaOverDays = ({
  storeRef,
  days,
  enabled = true,
}: {
  storeRef: string | null | undefined
  days: number
  enabled?: boolean
}): UseQueryResult<StoreCaOverDays, unknown> =>
  useQuery({
    queryKey: ['store-ca-over-days', storeRef, days],
    enabled: enabled && Boolean(storeRef) && Number.isInteger(days) && days > 0,
    queryFn: () => fetchStoreCaOverDays({ storeRef: storeRef!, days }),
  })

export const useCreateStoreProject = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createStoreProject,
    onSuccess: (newProject) => {
      queryClient.setQueryData<StoreProjectResponse[]>(['store-projects'], (old) => {
        if (!old) return [newProject]
        return [...old, newProject]
      })

      queryClient.invalidateQueries({ queryKey: ['store-projects'] })
      queryClient.invalidateQueries({ queryKey: ['real-estate-kpis'] })
    },
  })
}

export const useUpdateStoreProject = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateStoreProject,
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['store-projects'] })

      const previousProjects = queryClient.getQueryData<StoreProjectResponse[]>(['store-projects'])

      queryClient.setQueryData<StoreProjectResponse[]>(['store-projects'], (old) => {
        if (!old) return old
        return old.map((project) => (project.id === id ? { ...project, ...data } : project))
      })

      return { previousProjects }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(['store-projects'], context.previousProjects)
      }
    },
    onSuccess: (updatedProject) => {
      queryClient.setQueryData<StoreProjectResponse[]>(['store-projects'], (old) => {
        if (!old) return old
        return old.map((project) => (project.id === updatedProject.id ? updatedProject : project))
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['store-projects'] })
      queryClient.invalidateQueries({ queryKey: ['real-estate-kpis'] })
    },
  })
}

export const useDeleteStoreProject = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteStoreProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-projects'] })
    },
  })
}

export const useUpdateStoreProjectStatus = () => {
  const queryClient = useQueryClient()

  // Map status to statusLabel
  return useMutation({
    mutationFn: updateStoreProjectStatus,
    // Optimistic update: immediately update the cache before the API call completes
    onMutate: async ({ id, status }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['store-projects'] })

      // Snapshot the previous value for rollback
      const previousProjects = queryClient.getQueryData<StoreProjectResponse[]>(['store-projects'])

      // Optimistically update the cache immediately
      queryClient.setQueryData<StoreProjectResponse[]>(['store-projects'], (old) => {
        if (!old) return old

        return old.map((project) =>
          project.id === id
            ? {
                ...project,
                status,
              }
            : project,
        )
      })

      // Return context with the previous value for rollback
      return { previousProjects }
    },
    // If the mutation fails, rollback to the previous value
    onError: (_err, _variables, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(['store-projects'], context.previousProjects)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-projects'] })
      queryClient.invalidateQueries({ queryKey: ['real-estate-kpis'] })
    },
  })
}

export const useUpdateStoreProjectPosition = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateStoreProjectPosition,
    // Optimistic update: immediately reorder the cache before the API call completes
    onMutate: async ({ id, status, position }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['store-projects'] })

      // Snapshot the previous value for rollback
      const previousProjects = queryClient.getQueryData<StoreProjectResponse[]>(['store-projects'])

      // Optimistically update the cache immediately
      queryClient.setQueryData<StoreProjectResponse[]>(['store-projects'], (old) => {
        if (!old) return old

        // Find the project being moved
        const movingProject = old.find((p) => p.id === id)
        if (!movingProject) return old

        // Remove it from the current position
        const withoutMoving = old.filter((p) => p.id !== id)

        // Update its status
        const updatedProject = {
          ...movingProject,
          status,
        }

        // Group projects by status
        const projectsByStatus: Record<string, StoreProjectResponse[]> = {
          before_project: [],
          under_study: [],
          advanced_project: [],
          secured: [],
          works: [],
          openable: [],
          open: [],
        }

        // Organize existing projects by status
        withoutMoving.forEach((project) => {
          projectsByStatus[project.status].push(project)
        })

        // Insert the moved project at the new position in the target status
        projectsByStatus[status].splice(position, 0, updatedProject)

        // Reconstruct the array in the correct order (status order, then position within status)
        const statusOrder = [
          'before_project',
          'under_study',
          'advanced_project',
          'secured',
          'works',
          'openable',
          'open',
        ] as const
        const result: StoreProjectResponse[] = []
        statusOrder.forEach((statusKey) => {
          result.push(...projectsByStatus[statusKey])
        })

        return result
      })

      // Return context with the previous value for rollback
      return { previousProjects }
    },
    // If the mutation fails, rollback to the previous value
    onError: (_err, _variables, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(['store-projects'], context.previousProjects)
      }
    },
    // On success, invalidate to refetch and ensure consistency
    onSuccess: () => {
      // Invalidate in the background to get the server's authoritative order
      queryClient.invalidateQueries({ queryKey: ['store-projects'] })
    },
  })
}

export const useUploadStoreProjectPhoto = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: uploadStoreProjectPhoto,
    onSuccess: (data, variables) => {
      // Optimistically update the cache with the new photoUrls returned from the server
      queryClient.setQueryData<StoreProjectResponse[]>(['store-projects'], (old) => {
        if (!old) return old
        return old.map((project) =>
          project.id === variables.id ? { ...project, photoUrls: data.photoUrls } : project,
        )
      })

      // Also refetch in background to ensure full consistency
      queryClient.invalidateQueries({ queryKey: ['store-projects'] })
    },
  })
}

export const useDeleteStoreProjectPhoto = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteStoreProjectPhoto,
    onSuccess: (data, variables) => {
      // Optimistically update the cache with the new photoUrls returned from the server
      queryClient.setQueryData<StoreProjectResponse[]>(['store-projects'], (old) => {
        if (!old) return old
        return old.map((project) =>
          project.id === variables.id ? { ...project, photoUrls: data.photoUrls } : project,
        )
      })
    },
  })
}

export const useUploadStoreProjectDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: uploadStoreProjectDocument,
    onSuccess: (data, variables) => {
      // Optimistically update the cache with the new documents returned from the server
      queryClient.setQueryData<StoreProjectResponse[]>(['store-projects'], (old) => {
        if (!old) return old
        return old.map((project) =>
          project.id === variables.id ? { ...project, documents: data.documents } : project,
        )
      })

      // Also refetch in background to ensure full consistency
      queryClient.invalidateQueries({ queryKey: ['store-projects'] })
    },
  })
}

export const useDeleteStoreProjectDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteStoreProjectDocument,
    onSuccess: (data, variables) => {
      // Optimistically update the cache with the new documents returned from the server
      queryClient.setQueryData<StoreProjectResponse[]>(['store-projects'], (old) => {
        if (!old) return old
        return old.map((project) =>
          project.id === variables.id ? { ...project, documents: data.documents } : project,
        )
      })
    },
  })
}

export const useRenameStoreProjectDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: renameStoreProjectDocument,
    onSuccess: (data, variables) => {
      // Optimistically update the cache with the new documents returned from the server
      queryClient.setQueryData<StoreProjectResponse[]>(['store-projects'], (old) => {
        if (!old) return old
        return old.map((project) =>
          project.id === variables.id ? { ...project, documents: data.documents } : project,
        )
      })

      // Also refetch in background to ensure full consistency
      queryClient.invalidateQueries({ queryKey: ['store-projects'] })
    },
  })
}
